import * as React from 'react';
import { CheckCircle2, History, XCircle } from 'lucide-react';
import type { ImportCompletedPayload, ImportState } from '@klareospace/pharos-lib-types';
import { IMPORT_STATE } from '@klareospace/pharos-lib-types';
import { Button, FloatingNotificationCard } from '@klareospace/pharos-lib-web-utils';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { countRejected } from '../utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ImportCompletionPopupProps {
  onDismiss: () => void;
  payload: ImportCompletedPayload;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveIcon(state: ImportState): React.ReactNode {
  if (state === IMPORT_STATE.ERROR) {
    return <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />;
  }
  if (state === IMPORT_STATE.PARTIAL) {
    return <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />;
  }
  return <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />;
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Composes `FloatingNotificationCard` from an `ImportCompletedPayload` — success/partial/error copy, icon and navigation actions. */
export function ImportCompletionPopup({ onDismiss, payload }: ImportCompletionPopupProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('imports');
  const navigate = useNavigate();

  function handleViewReport(): void {
    onDismiss();
    void navigate({ params: { importId: payload.importId }, to: '/imports/history/$importId' });
  }

  function handleViewHistory(): void {
    onDismiss();
    void navigate({ to: '/imports/history' });
  }

  function buildSuccessSummary(): string {
    const { created, updated } = payload.linesCount;
    const rejected = countRejected(payload);
    const createdLabel = t('completionPopup.created', { count: created });
    const updatedLabel = updated > 0 ? t('completionPopup.updated', { count: updated }) : null;
    const errorsLabel = rejected > 0 ? t('completionPopup.errors', { count: rejected }) : t('completionPopup.noErrors');

    return `${[createdLabel, updatedLabel].filter(Boolean).join(', ')}, ${errorsLabel}.`;
  }

  function buildDescription(): string {
    if (payload.state === IMPORT_STATE.ERROR) {
      return `${t('completionPopup.rejected', { count: countRejected(payload) })}.`;
    }
    return buildSuccessSummary();
  }

  const title = payload.state === IMPORT_STATE.ERROR ? t('completionPopup.titleError') : t('completionPopup.title');

  return (
    <FloatingNotificationCard
      actions={
        <>
          <Button className="h-auto p-0 text-sm font-medium" onClick={handleViewReport} type="button" variant="link">
            {t('completionPopup.viewReport')}
          </Button>
          <Button
            className="h-auto gap-1 p-0 text-sm font-medium text-muted-foreground hover:text-foreground"
            onClick={handleViewHistory}
            type="button"
            variant="link"
          >
            <History className="h-3.5 w-3.5" />
            {tCommon('fields.importHistoryTitle')}
          </Button>
        </>
      }
      closeLabel={t('notificationCard.close')}
      description={buildDescription()}
      icon={resolveIcon(payload.state)}
      onClose={onDismiss}
      title={title}
    />
  );
}
